/**
 * Blog post utilities.
 *
 * BlogPost data is now fetched from Supabase (see useBlogs hooks).
 * This module provides the type, category list, and helper functions
 * for processing blog HTML (heading extraction, link rewriting).
 */

export type BlogPost = {
  slug: string;
  title: string;
  category: string;
  publishedAt: string;
  listingDate: string;
  readTime: string;
  excerpt: string;
  heroImage: string;
  listingImage: string;
  author: {
    name: string;
    image: string;
    bio: string;
  };
  bodyHtml: string;
};

export type BlogHeading = {
  id: string;
  text: string;
  level: 1 | 2 | 3;
};

export const blogCategories = [
  "Behind the Scenes",
  "Song Ideas",
  "Planning Tips",
  "Performance Tips",
] as const;

const blogContentCache = new Map<string, { html: string; headings: BlogHeading[] }>();

/** Map a Supabase blogs row to the BlogPost shape used by UI components. */
export function mapDbBlogToPost(row: {
  slug: string;
  title: string;
  category: string;
  published_at: string | null;
  listing_date: string | null;
  read_time: string | null;
  excerpt: string | null;
  hero_image: string | null;
  listing_image: string | null;
  author_name: string;
  author_image: string;
  author_bio: string;
  body_html: string;
}): BlogPost {
  return {
    slug: row.slug,
    title: row.title,
    category: row.category,
    publishedAt: row.published_at ?? "",
    listingDate: row.listing_date ?? "",
    readTime: row.read_time ?? "",
    excerpt: row.excerpt ?? "",
    heroImage: row.hero_image ?? "",
    listingImage: row.listing_image ?? "",
    author: {
      name: row.author_name,
      image: row.author_image,
      bio: row.author_bio,
    },
    bodyHtml: row.body_html,
  };
}

function normalizeHeadingText(text: string) {
  return text.replace(/\u200d/g, "").replace(/\s+/g, " ").trim();
}

function slugifyHeading(text: string, usedIds: Set<string>) {
  const normalized = text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s-]+/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

  const base = normalized || "section";
  let candidate = base;
  let suffix = 2;

  while (usedIds.has(candidate)) {
    candidate = `${base}-${suffix}`;
    suffix += 1;
  }

  usedIds.add(candidate);
  return candidate;
}

export function formatBlogReadTime(readTime: string) {
  if (!readTime) {
    return "";
  }

  return readTime.includes("min") ? readTime : `${readTime} min`;
}

export function getRelatedBlogPosts(posts: BlogPost[], currentSlug: string, limit = 3) {
  return posts.filter((post) => post.slug !== currentSlug).slice(0, limit);
}

export function getBlogPostContent(post: BlogPost) {
  const cached = blogContentCache.get(post.slug);

  if (cached) {
    return cached;
  }

  if (typeof DOMParser === "undefined") {
    const fallback = { html: post.bodyHtml, headings: [] as BlogHeading[] };
    blogContentCache.set(post.slug, fallback);
    return fallback;
  }

  const documentFragment = new DOMParser().parseFromString(post.bodyHtml, "text/html");
  const usedIds = new Set<string>();
  const headings: BlogHeading[] = [];

  documentFragment.body.querySelectorAll("h1, h2, h3").forEach((heading) => {
    const text = normalizeHeadingText(heading.textContent ?? "");

    if (!text) {
      return;
    }

    const id = slugifyHeading(text, usedIds);
    heading.id = id;
    headings.push({
      id,
      text,
      level: Number(heading.tagName.slice(1)) as 1 | 2 | 3,
    });
  });

  documentFragment.body.querySelectorAll("a").forEach((link) => {
    const href = link.getAttribute("href");

    if (href?.startsWith("https://www.bollyrhythm.com")) {
      link.setAttribute("href", href.replace("https://www.bollyrhythm.com", "") || "/");
    }

    if (href?.startsWith("https://bollyrhythm.com")) {
      link.setAttribute("href", href.replace("https://bollyrhythm.com", "") || "/");
    }
  });

  const content = {
    html: documentFragment.body.innerHTML,
    headings,
  };

  blogContentCache.set(post.slug, content);
  return content;
}
